/**
 * Whether a carousel is being dragged, or is still gliding to its snap.
 *
 * Every media decision on a page is geometry-driven: the proximity scheduler
 * measures every watched player and hands out sources, the autoplay provider
 * re-picks the one playing video as intersection ratios change, and HlsVideo
 * warms neighbours with a muted play(). Made per frame of a swipe, that was a
 * page-wide forced layout on each embla `scroll` event, plus a source attach
 * (hls.js + MSE setup) or a decoder start/stop whenever a card crossed a
 * boundary mid-gesture — the intermittent stutter on the hiives carousel.
 *
 * Two phases, because the two consumers need different things:
 * - `drag`: the finger is down. The playing video is not handed over while
 *   the track follows the finger; it is re-picked the moment the finger
 *   lifts, so the card being flung in starts during its glide and is already
 *   playing when it lands (waiting for rest here was a one-second pause).
 * - `travel`: drag plus the glide. The page-wide proximity pass waits for
 *   the track to come to rest. Meanwhile the carousel promotes the cards it
 *   is heading for by DOM position alone (`warmVideosIn`), with no layout
 *   read, so they are attaching sources before they land.
 *
 * Heavier work that only prepares for LATER — a neighbour's decode warm-up —
 * waits for the track to be quiet (`onceMediaMotionQuiet`): at rest, in the
 * last few pixels of a glide, or the instant a finger catches a moving track
 * (embla freezes it in place), so nothing on screen can visibly hitch.
 */
export type MediaMotionPhase = "drag" | "travel";

/**
 * A hold is only ever released by the carousel that took it. This bounds one
 * that never hears `settle`/`pointerUp` (a torn-down instance, an event lost
 * to a browser gesture takeover), so media can never stay frozen.
 */
const MAX_HOLD_MS = 6000;

const holds: Record<MediaMotionPhase, number> = { drag: 0, travel: 0 };
const settleListeners: Record<MediaMotionPhase, Set<() => void>> = {
  drag: new Set(),
  travel: new Set(),
};

export function isMediaMotionActive(phase: MediaMotionPhase = "travel") {
  return holds[phase] > 0;
}

/** Takes a hold on `phase`; the returned release is idempotent. */
export function holdMediaForMotion(phase: MediaMotionPhase = "travel") {
  holds[phase] += 1;
  let released = false;
  const timeout = setTimeout(() => release(), MAX_HOLD_MS);
  function release() {
    if (released) return;
    released = true;
    clearTimeout(timeout);
    holds[phase] -= 1;
    if (holds[phase] === 0) {
      for (const listener of settleListeners[phase]) listener();
      if (phase === "travel") emitQuiet();
    }
  }
  return release;
}

const quietListeners = new Set<() => void>();

function emitQuiet() {
  for (const listener of [...quietListeners]) listener();
}

/**
 * The track is moving too little for a late frame to show: a finger has
 * caught it (embla freezes it until the drag begins), or the glide is in its
 * last few pixels.
 */
export function markMediaMotionQuiet() {
  emitQuiet();
}

/**
 * Runs `listener` the next time the track is quiet — travel ended, a moving
 * track was caught, or a glide reached its last few pixels — then forgets it.
 * Returns a cancel.
 */
export function onceMediaMotionQuiet(listener: () => void) {
  const run = () => {
    quietListeners.delete(run);
    listener();
  };
  quietListeners.add(run);
  return () => {
    quietListeners.delete(run);
  };
}

/** Runs `listener` each time the last hold on `phase` is released. */
export function onMediaMotionSettle(
  listener: () => void,
  phase: MediaMotionPhase = "travel",
) {
  settleListeners[phase].add(listener);
  return () => {
    settleListeners[phase].delete(listener);
  };
}
